import { useTranslation } from 'react-i18next';
import {
  MarketplacePostInfluencer,
  MarketplaceAffiliateCommissionStatus,
  MarketplaceAffiliateCommissionInfluencer
} from '@anytag/graphql';
import { Button, CheckBox, TableCell, TextCutter } from '@anytag/shared-atoms';
import { Badge, BadgeProps } from '@anyui/core';
import { useSelectItemIds } from '@anytag/shared-hooks';
import { InfluencerAvatar } from '@anytag/shared-molecules';
import { cn, formatPrice } from '@anytag/shared-utils';
import { ColumnDef } from '@tanstack/react-table';

type SelectCommissionProps = Pick<
  ReturnType<typeof useSelectItemIds<number>>,
  'selectItem' | 'selectedItemIds' | 'hasAllItemsSelected' | 'toggleSelectAllItems'
>;

interface AffiliateInfluencersTableProps extends SelectCommissionProps {
  data: readonly MarketplaceAffiliateCommissionInfluencer[];
  onApprove: (influencer: MarketplacePostInfluencer) => void;
}

export const useMarketplaceCommissionsInfluencersTable = ({
  data,
  onApprove,
  selectItem,
  selectedItemIds,
  hasAllItemsSelected,
  toggleSelectAllItems
}: AffiliateInfluencersTableProps) => {
  const { t } = useTranslation();

  const getStatusColumnProps = (status: MarketplaceAffiliateCommissionStatus): BadgeProps => {
    switch (status) {
      case MarketplaceAffiliateCommissionStatus.APPROVED:
        return { children: t('Option.Approved'), color: 'green' };
      case MarketplaceAffiliateCommissionStatus.UNAPPROVED:
        return { children: t('Option.Not Approved'), color: 'zinc' };
      default:
        return { children: null, color: 'zinc' };
    }
  };

  const hasAllApproved = data.every((infl) => infl.status === MarketplaceAffiliateCommissionStatus.APPROVED);
  const columns: ColumnDef<MarketplaceAffiliateCommissionInfluencer>[] = [
    {
      accessorKey: 'fixedDate',
      header: () => (
        <div className="flex items-center gap-4">
          {!hasAllApproved ? (
            <CheckBox
              disabled={hasAllApproved}
              onClick={!hasAllApproved ? toggleSelectAllItems : undefined}
              help={t(hasAllItemsSelected ? 'Unselect all' : 'Select all')}
            />
          ) : null}
          <span>{t('HeaderColumn.Fixed date of commission')}</span>
        </div>
      ),
      cell: ({ row }) => {
        const { influencer, fixedDate, startCoverageDate, endCoverageDate, status } = row.original;
        const isSelected = selectedItemIds.includes(influencer.id);
        const isUnapproved = status === MarketplaceAffiliateCommissionStatus.UNAPPROVED;

        return (
          <div className="flex items-center gap-4">
            <CheckBox
              className="gap-4"
              checked={isSelected}
              disabled={!isUnapproved}
              key={`checkbox-${influencer.id}`}
              help={isSelected ? t('Unselect') : t('Select')}
              onClick={isUnapproved ? () => selectItem(influencer.id) : undefined} // without onClick just Children will be rendered
            />
            <TableCell.Text
              // BE gives us `yyyy-MM-DD` already
              value={
                <div className={cn({ 'ml-8': !isUnapproved && !hasAllApproved })}>
                  <span>{fixedDate}</span>&nbsp;
                  <span className="text-[11px] text-tertiary">{`(${startCoverageDate} - ${endCoverageDate})`}</span>
                </div>
              }
            />
          </div>
        );
      }
    },
    {
      accessorKey: 'influencer',
      header: t<string>('HeaderColumn.Influencer'),
      cell: ({ row }) => {
        const { influencer } = row.original;

        return (
          <div className="flex min-w-50 items-center gap-2">
            <InfluencerAvatar size="48px" id={influencer.id} src={influencer.avatar} className="bg-black-20" />
            <TextCutter lines={3} text={influencer.name} />
          </div>
        );
      }
    },
    {
      accessorKey: 'payout',
      header: t<string>('HeaderColumn.Payout'),
      cell: ({ row }) => (
        <TableCell.Text
          className="font-semibold"
          value={`${formatPrice(row.original.revenue, row.original.currency)} ${row.original.currency}`}
        />
      )
    },
    {
      accessorKey: 'status',
      header: t<string>('HeaderColumn.Status'),
      cell: ({ row }) => <Badge {...getStatusColumnProps(row.original.status)} />
    },
    {
      size: 50,
      accessorKey: 'approve',
      header: '',
      cell: ({ row }) =>
        row.original.status === MarketplaceAffiliateCommissionStatus.UNAPPROVED ? (
          <Button title={t('Button.Approve')} onClick={() => onApprove(row.original.influencer)} />
        ) : null
    }
  ];

  return { columns };
};
